import 'reflect-metadata';
import CareApp from './CareApp';
import ConsentApp from './ConsentsApp';
import ReportsApp from './ReportsApp';
import { MobileProvider } from 'utilities/MobileContext';
import { ToastProvider } from 'react-components/app/ReusableToast';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { routes } from './routing/AppRoutes';
import { Suspense, type ReactNode } from 'react';
import RouteLoadingFallback from './routing/RouteLoadingFallback';
import AppRouterTemplate from './routing/AppRouterTemplate';
import { getAppRoutesFromConfigs } from './routing/GetAppRoutesFromConfigs';
import { tvDashboardRouteConfig } from './routing/routes/TvDashboardRoutes';

/**
 * Host subdomain (e.g. `reports` from `reports.localhost:5670`).
 * Local reports UI: `http://reports.localhost:5670`.
 */
const subdomain = window.location.host.split('.')[0];

/**
 * Shared providers for Care Portal, Consent, and Reports entry points.
 * @param children - App tree under providers.
 */
const AppProviders = ({ children }: { children: ReactNode }) => (
  <MobileProvider>
    <ToastProvider>
      <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
    </ToastProvider>
  </MobileProvider>
);

/**
 * Care Portal AppWrapper — consent vs main care app on shared route trees.
 */
const CareAppWrapper = () => {
  return subdomain === 'consent' ? <ConsentApp /> : <CareApp />;
};

/**
 * Reports uses a dedicated router so `/new`, `/:reportId`, etc. match under the
 * reports subdomain (Care Portal child routes must not own those paths).
 */
const router =
  subdomain === 'reports'
    ? createBrowserRouter([
        {
          path: '*',
          element: (
            <AppProviders>
              <ReportsApp />
            </AppProviders>
          ),
        },
      ])
    : createBrowserRouter([
        {
          path: '/',
          element: (
            <AppProviders>
              <CareAppWrapper />
            </AppProviders>
          ),
          children: routes[0].children,
        },
        {
          path: '/tv-dashboard',
          element: (
            <AppProviders>
              <CareAppWrapper />
            </AppProviders>
          ),
          children: [
            {
              element: <AppRouterTemplate />,
              children: getAppRoutesFromConfigs([tvDashboardRouteConfig]),
            },
          ],
        },
        {
          path: '/:termKey',
          element: (
            <AppProviders>
              <ConsentApp />
            </AppProviders>
          ),
        },
      ]);

/**
 * Root Care Portal / Consent / Reports SPA entry.
 */
const App = () => {
  return <RouterProvider router={router} />;
};

export default App;
